import { useMutation } from '@apollo/client/react';
import useAgent from 'hooks/useAgent';
import { UPDATE_ACCOUNT_STATUS } from 'mutations/account';
import { useEffect, useState } from 'react';
import {
  ACCOUNT_STATUS_CODE,
  ACCOUNT_STATUS_OPERATION,
} from 'utilities/constants';
import { CARD_WATCH_REASON, TIERED_WATCH_REASON } from './applyStatusConstants';

export const useBypassStatus = ({
  activeStatuses = [],
  selectedStatusCode,
  selectedReasonCode,
  form,
}) => {
  const bypassStatusesToBeReplaced =
    selectedStatusCode === ACCOUNT_STATUS_CODE.TIERED_WATCH &&
    selectedReasonCode === TIERED_WATCH_REASON.SECONDARY_BYPASS
      ? activeStatuses.filter(
          (status) =>
            (status.statusCode === ACCOUNT_STATUS_CODE.TIERED_WATCH &&
              status.reasonCode === TIERED_WATCH_REASON.ADS_II_BYPASS) ||
            (status.statusCode === ACCOUNT_STATUS_CODE.CARD_WATCH &&
              status.reasonCode === CARD_WATCH_REASON.FRAUD_ALERT_BYPASS),
        )
      : [];

  const agent = useAgent();
  const [statusOverrideWarning, setStatusOverrideWarning] = useState('');
  const [removingBypassStatus, setRemovingBypassStatus] = useState('');

  const [setAccountFraudStatus] = useMutation(UPDATE_ACCOUNT_STATUS);

  const handleBypassStatuses = async ({ customerId, accountIds }) => {
    const hasCW20 = bypassStatusesToBeReplaced?.find(
      (status) => status.statusCode === ACCOUNT_STATUS_CODE.CARD_WATCH,
    );

    // if applying TW19, remove existing bypass statuses (TW15, CW20) first
    // ignore if account only has TW15
    let bypassStatusRemovalResponse;
    if (bypassStatusesToBeReplaced?.length > 1 || hasCW20) {
      bypassStatusRemovalResponse = await Promise.all(
        bypassStatusesToBeReplaced.map((status) => {
          const fullStatusCode = `${status.statusCode}_${status.reasonCode}`;

          return setAccountFraudStatus({
            variables: {
              customerId,
              accountIds,
              agentName: agent?.user?.name || 'anonymous',
              operation: ACCOUNT_STATUS_OPERATION.REMOVED,
              reasonCode: fullStatusCode,
              statusCode: status.statusCode,
            },
          });
        }),
      );
    }

    return bypassStatusRemovalResponse;
  };

  const handleStatusOverrideWarningChange = (statusCode) => {
    if (bypassStatusesToBeReplaced?.length) {
      const fullStatusCode = bypassStatusesToBeReplaced
        .map((status) => `${status.statusCode}-${status.reasonCode}`)
        .join(bypassStatusesToBeReplaced.length > 1 ? ', ' : '');

      setRemovingBypassStatus(fullStatusCode);
      return setStatusOverrideWarning(
        `Applying this status will replace ${fullStatusCode}. Are you sure you want to proceed?`,
      );
    }

    // warn about status replacement
    const existingStatusCode = activeStatuses.find((status) => {
      return status.statusCode === statusCode;
    });

    if (existingStatusCode) {
      return setStatusOverrideWarning(
        `Applying this status will replace ${existingStatusCode.description}. Are you sure you want to proceed?`,
      );
    } else {
      setStatusOverrideWarning('');
    }
  };

  useEffect(() => {
    setRemovingBypassStatus('');
    form.validateFields();
    handleStatusOverrideWarningChange(selectedStatusCode, selectedReasonCode);
  }, [selectedStatusCode, selectedReasonCode, activeStatuses]);

  return {
    bypassStatusesToBeReplaced,
    handleBypassStatuses,
    statusOverrideWarning,
    setStatusOverrideWarning,
    removingBypassStatus,
    setRemovingBypassStatus,
  };
};
